import AuthCode from "react-auth-code-input";
import { Control, Controller, type FieldValues } from "react-hook-form";
import { styled } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";

import { colors } from "@covertree/theme/colors";

interface FormCodeFieldProps<TValues extends FieldValues> {
  id: string;
  name: string;
  label?: string;
  control: Control<TValues>;
  dataTestId?: string;
  disabled?: boolean;
  className?: string;
}

const CodeField = <TValues extends FieldValues>({
  id,
  name,
  label,
  control,
  className,
}: FormCodeFieldProps<TValues>) => {
  return (
    <Controller
      /* eslint-disable  @typescript-eslint/no-explicit-any */
      name={name as any}
      control={control}
      render={({ field: { value, ...rest }, fieldState }) => (
        <FormControl
          variant="standard"
          id={`${id}-container`}
          className={className}>
          <FormLabel>{label}</FormLabel>{" "}
          <AuthCode
            allowedCharacters="numeric"
            inputClassName="auth-code-input"
            containerClassName="auth-code-container"
            {...rest}
          />
          <FormHelperText
            variant="outlined"
            error
            {...(!fieldState.error?.message && { sx: { margin: 0 } })}>
            {fieldState.error?.message}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const FormCodeField = styled(CodeField)(({ theme }) => ({
  ".auth-code-container": {
    "& > :first-child": {
      marginRight: theme.spacing(0.5),
      [theme.breakpoints.down("sm")]: {
        marginRight: 3,
      },
    },
    "& > :last-child": {
      marginLeft: theme.spacing(0.5),
      [theme.breakpoints.down("sm")]: {
        marginLeft: 3,
      },
    },
    "& > :not(:first-child):not(:last-child)": {
      margin: theme.spacing(0, 0.5, 0, 0.5),
      [theme.breakpoints.down("sm")]: {
        margin: "0 3px",
      },
    },
  },
  ".auth-code-input": {
    height: theme.spacing(8),
    width: theme.spacing(7.25),
    [theme.breakpoints.down("sm")]: {
      width: theme.spacing(6),
    },

    border: `1px solid ${colors.grey3}`,
    borderRadius: "15%",

    fontFamily: theme.typography.fontFamily,
    textAlign: "center",
    ...theme.typography.body1,

    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      appearance: "none",
      margin: 0,
    },

    "&[type=number]": {
      appearance: "textfield",
    },

    "&:focus": {
      outline: "none",
      border: `2px solid ${colors.green3}`,
    },
  },
})) as typeof CodeField;

export default FormCodeField;
